// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useCallback } from "react";
import { distance, GRID_SIZE } from "../utils/scenarioEditorUtils";

// Get nearest vertex to point
export const useGetNearestVertex = () => {
  return useCallback((polygon, point, threshold = 8) => {
    if (!polygon || !polygon.points || polygon.points.length === 0) return null;
    let nearestIndex = null;
    let nearestDist = Infinity;
    polygon.points.forEach((p, index) => {
      const dist = distance(p, point);
      if (dist < nearestDist && dist <= threshold) {
        nearestDist = dist;
        nearestIndex = index;
      }
    });
    return nearestIndex;
  }, []);
};

// Point to segment distance
export const usePointToSegmentDistance = () => {
  return useCallback((p, a, b) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return distance(p, a);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = a.x + t * dx;
    const cy = a.y + t * dy;
    return distance(p, { x: cx, y: cy });
  }, []);
};

// Get nearest edge to point
export const useGetNearestEdge = (pointToSegmentDistance) => {
  return useCallback(
    (polygon, point, threshold = 8) => {
      if (!polygon || !polygon.points || polygon.points.length < 2) return null;
      let nearestIndex = null;
      let nearestDist = Infinity;
      const points = polygon.points;
      for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        const dist = pointToSegmentDistance(point, a, b);
        if (dist < nearestDist && dist <= threshold) {
          nearestDist = dist;
          nearestIndex = i;
        }
      }
      return nearestIndex;
    },
    [pointToSegmentDistance],
  );
};

// Get element bounds
export const useGetElementBounds = () => {
  return useCallback((element) => {
    if (!element) return { x: 0, y: 0, width: 0, height: 0 };
    switch (element.type) {
      case "rect":
        return {
          x: element.x,
          y: element.y,
          width: element.width,
          height: element.height,
        };
      case "circle":
      case "tree":
      case "trash":
      case "fountain_drink":
      case "lighting":
      case "info_board":
      case "fountain_water":
        return {
          x: element.cx - element.r,
          y: element.cy - element.r,
          width: element.r * 2,
          height: element.r * 2,
        };
      case "stage":
      case "seating":
      case "parking":
      case "toilet":
      case "fence":
      case "entrance":
        return {
          x: element.x,
          y: element.y,
          width: element.width,
          height: element.height,
        };
      case "text": {
        const textWidth =
          (element.text?.length || 0) * (element.fontSize * 0.6);
        return {
          x: element.x - 5,
          y: element.y - element.fontSize,
          width: textWidth + 10,
          height: element.fontSize + 5,
        };
      }
      case "pin":
        return { x: element.x, y: element.y, width: 20, height: 30 };
      case "polygon": {
        if (!element.points || element.points.length === 0) {
          return { x: 0, y: 0, width: 0, height: 0 };
        }
        const xs = element.points.map((p) => p.x || p.lng || 0);
        const ys = element.points.map((p) => p.y || p.lat || 0);
        return {
          x: Math.min(...xs),
          y: Math.min(...ys),
          width: Math.max(...xs) - Math.min(...xs),
          height: Math.max(...ys) - Math.min(...ys),
        };
      }
      default:
        return { x: 0, y: 0, width: 0, height: 0 };
    }
  }, []);
};

// Check if point is in bounds
export const useIsPointInBounds = () => {
  return useCallback((point, bounds) => {
    return (
      point.x >= bounds.x &&
      point.x <= bounds.x + bounds.width &&
      point.y >= bounds.y &&
      point.y <= bounds.y + bounds.height
    );
  }, []);
};

// Snap to grid
export const useSnapToGridCoords = (snapToGrid) => {
  return useCallback(
    (coords) => {
      if (!snapToGrid) return coords;
      return {
        x: Math.round(coords.x / GRID_SIZE) * GRID_SIZE,
        y: Math.round(coords.y / GRID_SIZE) * GRID_SIZE,
      };
    },
    [snapToGrid],
  );
};

// Get SVG coordinates from event
export const useGetSVGCoordinates = (snapToGridCoords) => {
  return useCallback(
    (e, svgRef) => {
      if (!svgRef.current) return { x: 0, y: 0 };
      const svg = svgRef.current;
      const point = svg.createSVGPoint();
      point.x = e.clientX;
      point.y = e.clientY;
      const ctm = svg.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      const svgPoint = point.matrixTransform(ctm.inverse());
      return snapToGridCoords({ x: svgPoint.x, y: svgPoint.y });
    },
    [snapToGridCoords],
  );
};

// Coordinate conversion utilities (OSM <-> SVG)
export const usePixelToLngLat = (CANVAS_WIDTH, CANVAS_HEIGHT) => {
  return useCallback(
    (x, y, center, zoom, tileSize = 256) => {
      if (!center) return { lng: 0, lat: 0 };
      const scale = tileSize * Math.pow(2, zoom);
      const lng = center.lng + ((x - CANVAS_WIDTH / 2) / scale) * 360;
      const lat = center.lat - ((y - CANVAS_HEIGHT / 2) / scale) * 360;
      return { lng, lat };
    },
    [CANVAS_WIDTH, CANVAS_HEIGHT],
  );
};

export const useLngLatToPixel = (CANVAS_WIDTH, CANVAS_HEIGHT) => {
  return useCallback(
    (lng, lat, center, zoom, tileSize = 256) => {
      if (!center) return { x: 0, y: 0 };
      const scale = tileSize * Math.pow(2, zoom);
      const x = CANVAS_WIDTH / 2 + ((lng - center.lng) * scale) / 360;
      const y = CANVAS_HEIGHT / 2 - ((lat - center.lat) * scale) / 360;
      return { x, y };
    },
    [CANVAS_WIDTH, CANVAS_HEIGHT],
  );
};
